import React, { useEffect, useState } from "react";
import { api } from "../../api";
import { useLanguage } from "../../i18n";
import { ExpensesSection } from "./ExpensesSection";

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

interface Entry {
  month: string;
  amount: number;
  status: "paid" | "pending";
  mode: string | null;
  paidOn: string | null;
  tenantName: string;
  propertyLabel: string;
}

interface Summary {
  totals: { collected: number; pending: number; paidCount: number; totalCount: number };
  monthly: { month: string; collected: number; pending: number }[];
  byProperty: { propertyLabel: string; collected: number; pending: number }[];
  entries: Entry[];
}

function monthLabel(m: string) {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "short" });
}

function exportCsv(entries: Entry[]) {
  const header = ["Property", "Tenant", "Month", "Amount", "Status", "Mode", "Paid On"];
  const rows = entries.map((e) => [
    e.propertyLabel,
    e.tenantName,
    e.month,
    String(e.amount),
    e.status,
    e.mode ?? "",
    e.paidOn ?? "",
  ]);
  const csv = [header, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `toletbook-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function OwnerReports({ onBack }: { onBack: () => void }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    api.get("/api/reports/summary").then(setSummary);
  }, []);

  const header = (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <button className="icon-btn" onClick={onBack} aria-label="Back">‹</button>
      <div className="section-title" style={{ marginBottom: 0 }}>{t("ownerReports.heading")}</div>
    </div>
  );

  if (!summary) {
    return (
      <>
        {header}
        <div className="spinner" />
      </>
    );
  }
  const { totals, monthly, byProperty, entries } = summary;

  if (totals.totalCount === 0) {
    return (
      <>
        {header}
        <div className="empty-state">{t("ownerReports.emptyState")}</div>
        <ExpensesSection />
      </>
    );
  }

  const maxMonthly = Math.max(1, ...monthly.map((m) => m.collected + m.pending));
  const onTimePct = totals.totalCount > 0 ? Math.round((totals.paidCount / totals.totalCount) * 100) : 0;

  return (
    <>
      {header}

      <div style={{ display: "flex", gap: 22, marginBottom: 20 }}>
        <div>
          <div className="reg-eyebrow" style={{ marginBottom: 5 }}>{t("ownerReports.collected")}</div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{fmtINR(totals.collected)}</div>
        </div>
        <div>
          <div className="reg-eyebrow" style={{ marginBottom: 5 }}>{t("ownerReports.pending")}</div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", color: "var(--danger)" }}>
            {fmtINR(totals.pending)}
          </div>
        </div>
        <div>
          <div className="reg-eyebrow" style={{ marginBottom: 5 }}>{t("ownerReports.paidOnRecord")}</div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{onTimePct}%</div>
        </div>
      </div>

      {monthly.length > 0 && (
        <>
          <div className="section-title" style={{ margin: "0 0 10px", fontSize: 12 }}>
            {t("ownerReports.collectionsByMonth")}
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 110, marginBottom: 6 }}>
            {monthly.map((m) => (
              <div key={m.month} style={{ flex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 6 }}>
                <div
                  title={t("ownerReports.monthTooltip", { month: m.month, collected: fmtINR(m.collected), pending: fmtINR(m.pending) })}
                  style={{
                    width: "100%",
                    borderRadius: 4,
                    background: "var(--brand-700)",
                    height: `${Math.max(4, (m.collected / maxMonthly) * 100)}%`,
                  }}
                />
                {m.pending > 0 && (
                  <div
                    style={{
                      width: "100%",
                      borderRadius: 4,
                      background: "var(--danger)",
                      height: `${Math.max(4, (m.pending / maxMonthly) * 100)}%`,
                      marginTop: -6,
                    }}
                  />
                )}
                <div style={{ fontSize: 10.5, textAlign: "center", color: "var(--ink-faint)" }}>{monthLabel(m.month)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="section-title" style={{ margin: "22px 0 8px", fontSize: 12 }}>
        {t("ownerReports.byProperty")}
      </div>
      {byProperty.map((p) => (
        <div className="card-row" key={p.propertyLabel} style={{ padding: "10px 0", borderBottom: "1px solid var(--line)" }}>
          <span className="sub">{p.propertyLabel}</span>
          <div style={{ textAlign: "right" }}>
            <b>{fmtINR(p.collected)}</b>
            {p.pending > 0 && (
              <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 2 }}>{t("ownerReports.pendingAmount", { amount: fmtINR(p.pending) })}</div>
            )}
          </div>
        </div>
      ))}

      <button className="btn-solid" style={{ marginTop: 22 }} onClick={() => exportCsv(entries)}>
        {t("ownerReports.exportCsvButton")}
      </button>
      <div className="field-hint" style={{ textAlign: "center", marginTop: 8 }}>
        {t("ownerReports.hint")}
      </div>

      <ExpensesSection />
    </>
  );
}
